/**
 * PillarBpa — the Best Practice tile on the Command Center (mockup 01
 * pillar row).
 *
 * Data:
 *   - useComplianceDashboard() → frameworks (score and the Guardian Score
 *     flag) and the failing-control counts by severity.
 *   - usePriorityActions(bpa) → the ranked top item, named in the finding.
 *
 * The score is `bpaStarredScore`: a weighted average of the frameworks the
 * tenant scores in the Guardian Score, the same number the brief and the
 * Guardian Score waffle read (`dashboardData.overallScore` averages every
 * assigned framework, which disagreed with the brief).
 *
 * Failing counts follow the known-flag rule (blueprint §5.2 wiring note):
 * they render only when `totalViolationsKnown`, so a failed summary never
 * reads as zero failing controls. Severity is three tiers, Critical, High
 * and Medium; there is no Low tier for controls (§7 C14).
 */

import { useMemo, type ReactNode } from 'react'
import { calculateStarredBpaScore, useComplianceDashboard } from '@/hooks/useComplianceData'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import { SegmentedMeter, Skeleton } from '@/components/carbon'
import { PillarTile } from './PillarShell'
import { BpaEmpty } from './EmptyStates'
import { firstStep, plural } from './cc-queue'

interface Props {
  tenantId: string | undefined
  /** The delta tag ("+4 in 90 days"), from the score history (cc-history.ts). */
  delta?: ReactNode
}

const TITLE = 'Best Practice'

export default function PillarBpa({ tenantId, delta }: Props) {
  const { dashboardData, isLoading } = useComplianceDashboard(tenantId)
  const { actions, error: actionsError } = usePriorityActions({
    tenantId,
    maxCount: 3,
    pillar: 'bpa',
  })

  // Empty when no framework is assigned (zero data, not "100% healthy").
  const isEmpty = !isLoading && (!dashboardData || dashboardData.frameworks.length === 0)

  const starredScore = useMemo(() => {
    if (!dashboardData?.frameworks) return null
    return calculateStarredBpaScore(
      dashboardData.frameworks.map(f => ({
        code: f.code,
        score: f.score,
        weight: f.weight,
        includeInGuardianScore: f.includeInGuardianScore,
      })),
    )
  }, [dashboardData?.frameworks])

  if (isEmpty) {
    return (
      <PillarTile title={TITLE} category="bpa" to="/security?tab=best-practices" linkLabel="Open Security Center" score={null} delta={delta}>
        <BpaEmpty />
      </PillarTile>
    )
  }

  // Counts render only when the source query succeeded (the known flag), never as a placeholder 0.
  const counts = dashboardData?.totalViolationsKnown
    ? {
        failing: dashboardData.totalViolations,
        critical: dashboardData.criticalViolations,
        high: dashboardData.highViolations,
        medium: dashboardData.mediumViolations,
      }
    : null
  const failing = counts?.failing ?? null
  const critical = counts?.critical ?? null
  const next = actionsError ? null : firstStep(actions[0]?.title)

  let finding: string | null = null
  if (!isLoading && dashboardData && !counts) {
    finding = 'Failing-control counts did not load. Refresh the page to try again.'
  } else if (failing != null && failing > 0) {
    const crit = critical != null && critical > 0 && critical <= failing ? `, and ${critical.toLocaleString('en-US')} of them ${critical === 1 ? 'is' : 'are'} critical` : ''
    finding = `${plural(failing, 'control is', 'controls are')} failing${crit}.${next ? ` ${next}` : ''}`
  } else if (failing === 0) {
    finding = 'Nothing to review. No control is failing in the frameworks you score.'
  }

  const ariaLabel = [starredScore == null ? TITLE : `${TITLE}, ${Math.round(starredScore)}`, finding].filter(Boolean).join('. ')

  return (
    <PillarTile
      title={TITLE}
      category="bpa"
      to="/security?tab=best-practices"
      linkLabel="Open Security Center"
      score={starredScore}
      isLoading={isLoading}
      delta={delta}
      finding={isLoading ? <Skeleton lines={2} /> : finding}
      ariaLabel={isLoading ? TITLE : ariaLabel}
      viz={
        isLoading || !counts ? null : (
          <SegmentedMeter
            caption="Failing controls by severity"
            segments={[
              { id: 'crit', value: counts.critical, tone: 'crit', label: 'critical', glyph: true },
              { id: 'high', value: counts.high, tone: 'high', label: 'high', glyph: true },
              { id: 'med', value: counts.medium, tone: 'med', label: 'medium', glyph: true },
            ]}
          />
        )
      }
    />
  )
}
